import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Flex,
  Popover,
  Text,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  handleSpec,
  type IChartCell,
  toggleChartCell,
} from "src/redux/slices/entriesGeneratorSlice";
import { getObjectKeys } from "src/utils";

const SelectedColumnsSummary = () => {
  const dispatch = useDispatch();
  const [opened, setOpened] = useState(false);
  const { selectedCols, selectedCells, logbooks, sections } = useSelector(
    state => state.entriesGeneratorSlice,
  );

  const groups = useMemo(
    () =>
      [
        ...new Set([
          ...getObjectKeys(selectedCols),
          ...getObjectKeys(selectedCells),
        ]),
      ]
        .map(logId => {
          const logbookLabel =
            logbooks.find(log => log.value === logId)?.label ?? logId;

          const sectionGroups = [
            ...new Set(
              [
                ...getObjectKeys(selectedCols[logId] ?? {}),
                ...getObjectKeys(selectedCells[logId] ?? {}),
              ].map(Number),
            ),
          ]
            .map(sIndex => {
              const cols = selectedCols[logId]?.[sIndex] ?? [];
              const cells = selectedCells[logId]?.[sIndex] ?? [];
              const sectionTitle =
                sections[logId]?.[Number(sIndex)]?.title ??
                `Section ${Number(sIndex) + 1}`;

              return { sIndex: Number(sIndex), sectionTitle, cols, cells };
            })
            .filter(
              section => section.cols.length > 0 || section.cells.length > 0,
            );

          return { logId, logbookLabel, sectionGroups };
        })
        .filter(log => log.sectionGroups.length > 0),
    [selectedCols, selectedCells, logbooks, sections],
  );

  const totalCount = useMemo(
    () =>
      groups.reduce(
        (sum, log) =>
          sum +
          log.sectionGroups.reduce(
            (s, sec) => s + sec.cols.length + sec.cells.length,
            0,
          ),
        0,
      ),
    [groups],
  );

  // Selection is deduped by exact column name within a section, so two
  // identically-named columns in the same section can't be told apart here.
  const handleRemove = useCallback(
    (logId: string, sIndex: number, col: string) => {
      dispatch(handleSpec({ section: sIndex, col, logbook: logId }));
    },
    [dispatch],
  );

  const handleRemoveCell = useCallback(
    (logId: string, sIndex: number, cell: IChartCell) => {
      dispatch(toggleChartCell({ logbook: logId, section: sIndex, cell }));
    },
    [dispatch],
  );
  const hasCells = groups.some(log =>
    log.sectionGroups.some(section => section.cells.length > 0),
  );

  if (totalCount === 0) return null;

  return (
    <Popover opened={opened} onChange={setOpened} withinPortal>
      <Popover.Target>
        <Button
          size="sm"
          variant="light"
          onClick={() => setOpened(prev => !prev)}>
          {hasCells
            ? `${totalCount} selected`
            : `${totalCount} column${totalCount === 1 ? "" : "s"} selected`}
        </Button>
      </Popover.Target>
      <Popover.Dropdown maw={360} mah={400} style={{ overflowY: "auto" }}>
        {groups.map(log => (
          <Fragment key={log.logId}>
            <Text fw={600} size="sm" mt={4}>
              {log.logbookLabel}
            </Text>
            {log.sectionGroups.map(section => (
              <div key={section.sIndex} style={{ marginLeft: 8 }}>
                <Text size="xs" c="dimmed" mt={4}>
                  {section.sectionTitle}
                </Text>
                <Flex wrap="wrap" gap={4} mt={2} mb={4}>
                  {section.cols.map(col => (
                    <Badge
                      key={col}
                      variant="outline"
                      rightSection={
                        <ActionIcon
                          size="xs"
                          variant="transparent"
                          onClick={() =>
                            handleRemove(log.logId, section.sIndex, col)
                          }
                          aria-label={`Remove ${col}`}>
                          <IconX size={10} />
                        </ActionIcon>
                      }>
                      {col.replaceAll("<br>", " ")}
                    </Badge>
                  ))}
                  {section.cells.map(cell => (
                    <Badge
                      key={cell.cellKey}
                      variant="outline"
                      rightSection={
                        <ActionIcon
                          size="xs"
                          variant="transparent"
                          onClick={() =>
                            handleRemoveCell(log.logId, section.sIndex, cell)
                          }
                          aria-label={`Remove ${cell.name}`}>
                          <IconX size={10} />
                        </ActionIcon>
                      }>
                      {cell.name}
                    </Badge>
                  ))}
                </Flex>
              </div>
            ))}
            <Divider my={4} />
          </Fragment>
        ))}
      </Popover.Dropdown>
    </Popover>
  );
};

export default SelectedColumnsSummary;
